import {UserStore} from 'stores/userStore';
import {createContext, useContext} from 'react';
import {OnboardingStore} from 'stores/onboardingStore';
import {PlagiarismStore} from 'stores/plagiarismStore';
import {HistoryStore} from 'stores/historyStore';
import {SocialMediaStore} from './socialMedia';
import {AnnouncementStore} from 'stores/announcementStore';
import {makeAutoObservable} from 'mobx';
import {InputStore} from 'stores/inputStore';
import {BatchFileStore} from 'stores/batchFileStore';
import {ModalStore} from 'stores/modalStore';
import {ScanStore} from './aiScanStore';
import {GrammarScanStore} from 'stores/grammarScanStore';
import {CertificateStore} from 'stores/certificateStore';
import {LayoutStore} from "stores/layoutStore";
import {HumanizerStore} from 'stores/humanizerStore';
import {SubscriptionStore} from 'stores/subscriptionStore';
import {PricingStore} from 'stores/pricingStore';
import {ToastStore} from 'stores/toastStore';
import type {FluentVariable} from "@fluent/bundle";
import {translate} from 'i18n/translator';
import {LocalizedText, resolveText} from 'i18n/text';

export type AppError = { message: LocalizedText; code: string; translationArgs?: Record<string, FluentVariable> } | null;

export class RootStore {
    layout: LayoutStore;
    user: UserStore;
    subscription: SubscriptionStore;
    pricing: PricingStore;
    aiScan: ScanStore;
    grammar: GrammarScanStore;
    certificate: CertificateStore;
    input: InputStore;
    humanizer: HumanizerStore;
    history: HistoryStore;
    modal: ModalStore;
    toast: ToastStore;
    onboarding: OnboardingStore;
    plagiarism: PlagiarismStore;
    socialMedia: SocialMediaStore;
    batchFile: BatchFileStore;
    announcement: AnnouncementStore;

    error: AppError = null; // {message: string, code: string}
    isLoading: boolean = false;
    widgetMode: boolean = false;
    reportViewMode: boolean = false;
    humanizeEnabled: boolean = false;
    /** The widget's host asked for the app's own flow (`?flow=app`, docs/WIDGET_EMBED.md). */
    widgetAppFlow: boolean = false;

    constructor() {
        this.user = new UserStore(this);
        this.subscription = new SubscriptionStore(this);
        this.pricing = new PricingStore(this);
        this.aiScan = new ScanStore(this);
        this.grammar = new GrammarScanStore(this);
        this.plagiarism = new PlagiarismStore(this);
        this.certificate = new CertificateStore(this);
        this.batchFile = new BatchFileStore(this);
        this.input = new InputStore(this);
        this.humanizer = new HumanizerStore(this);
        this.history = new HistoryStore(this);
        this.modal = new ModalStore(this);
        this.toast = new ToastStore();
        this.onboarding = new OnboardingStore(this);
        this.socialMedia = new SocialMediaStore(this);
        this.layout = new LayoutStore(this);
        this.announcement = new AnnouncementStore(this);
        makeAutoObservable(this);
    }

    getUser() {
        return this.user.user;
    }

    async doRefresh() {
        await this.user.refresh();
        if (!this.user.isAnonymous) {
            await this.history.loadHistory({quiet: true});
            this.socialMedia.refreshSocial();
        }
    }

    async refresh() {
        try {
            await this.doRefresh();
        } catch (err) {
            // swallow; child stores handle their own errors
        }
    }

    setError(error?: AppError | LocalizedText | null) {
        if (typeof error === 'string' || typeof error === 'function') {
            error = {message: error, code: 'unknown'};
        }
        if (error) {
            console.error('Root error', error.code, resolveText(error.message));
        }
        this.error = error === undefined ? null : error;
    }

    /** The error's copy in the current language: the error_type__<code> string when
     *  there is one, the message otherwise. */
    get errorMessage(): string | null {
        const error = this.error;
        if (!error) return null;
        const message = resolveText(error.message);
        return error.code && error.code !== 'unknown'
            ? translate(`error_type__${error.code}`, error.translationArgs, message)
            : message;
    }

    setLoading(loading: boolean) {
        this.isLoading = loading;
        if (loading) {
            this.modal.closeModal();
        }
    }
}

export const rootStore = new RootStore();

;(window as any).root = rootStore;

export const StoreContext = createContext<RootStore>(rootStore);

export const useStores = () => useContext(StoreContext);
